import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setScale(1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setScale(1);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const currentImage = images[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-[110] group"
        data-testid="lightbox-close"
      >
        <X size={24} className="text-white group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Zoom Controls */}
      <div className="absolute top-6 left-6 flex space-x-2 z-[110]">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoomIn();
          }}
          className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all"
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn size={20} className="text-white" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoomOut();
          }}
          className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all"
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut size={20} className="text-white" />
        </button>
      </div>

      {/* Navigation - Previous */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          className="absolute left-6 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-[110]"
          data-testid="lightbox-previous"
        >
          <ChevronLeft size={32} className="text-white" />
        </button>
      )}

      {/* Navigation - Next */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-6 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-[110]"
          data-testid="lightbox-next"
        >
          <ChevronRight size={32} className="text-white" />
        </button>
      )}

      {/* Image */}
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-7xl max-h-[90vh] p-12 flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.img
          src={currentImage.image_url}
          alt={currentImage.title}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          style={{ transform: `scale(${scale})`, transition: 'transform 0.3s ease' }}
          data-testid="lightbox-image"
        />
        <div className="mt-6 text-center">
          <h3 className="text-2xl font-heading font-bold text-white mb-2">{currentImage.title}</h3>
          <p className="text-gray-400 text-sm">{currentImage.description}</p>
          <p className="text-primary text-xs uppercase tracking-wider mt-2">
            {currentImage.category} • {currentIndex + 1} / {images.length}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImageLightbox;
